/**
 * Widgets Hook
 * ვიჯეტების ჰუკი
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getWidgetConfigs,
  getWidgetConfig,
  getDefaultWidgetConfig,
  createWidgetConfig,
  updateWidgetConfig,
  setDefaultWidgetConfig,
  deleteWidgetConfig,
  enableWidgetConfig,
  disableWidgetConfig,
  assignWidgetPrompt,
  unassignWidgetPrompt,
  setWidgetTools,
  setWidgetFiles,
  type CreateWidgetConfigRequest,
  type UpdateWidgetConfigRequest,
} from '../features/api/widgets';

export function useWidgets() {
  const query = useQuery({
    queryKey: ['widgets'],
    queryFn: () => getWidgetConfigs(),
  });

  return {
    widgets: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useWidget(id: string) {
  const query = useQuery({
    queryKey: ['widget', id],
    queryFn: () => getWidgetConfig(id),
    enabled: !!id,
  });

  return {
    widget: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useDefaultWidget() {
  const query = useQuery({
    queryKey: ['widget', 'default'],
    queryFn: () => getDefaultWidgetConfig(),
  });

  return {
    widget: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

export function useCreateWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateWidgetConfigRequest) => createWidgetConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
    },
  });
}

export function useUpdateWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateWidgetConfigRequest }) =>
      updateWidgetConfig(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      queryClient.invalidateQueries({ queryKey: ['widget', id] });
    },
  });
}

export function useSetDefaultWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => setDefaultWidgetConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      queryClient.invalidateQueries({ queryKey: ['widget', 'default'] });
    },
  });
}

export function useToggleWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      enabled ? disableWidgetConfig(id) : enableWidgetConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
    },
  });
}

export function useDeleteWidget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteWidgetConfig(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
    },
  });
}

// ============== Prompt Management ==============

export function useAssignWidgetPrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ widgetId, promptId }: { widgetId: string; promptId: string }) =>
      assignWidgetPrompt(widgetId, promptId),
    onSuccess: (_, { widgetId }) => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      queryClient.invalidateQueries({ queryKey: ['widget', widgetId] });
    },
  });
}

export function useUnassignWidgetPrompt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (widgetId: string) => unassignWidgetPrompt(widgetId),
    onSuccess: (_, widgetId) => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      queryClient.invalidateQueries({ queryKey: ['widget', widgetId] });
    },
  });
}

// ============== Tool Management ==============

export function useSetWidgetTools() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ widgetId, toolIds }: { widgetId: string; toolIds: string[] }) =>
      setWidgetTools(widgetId, toolIds),
    onSuccess: (_, { widgetId }) => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      queryClient.invalidateQueries({ queryKey: ['widget', widgetId] });
    },
  });
}

// ============== File Management (RAG) ==============

export function useSetWidgetFiles() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ widgetId, fileIds }: { widgetId: string; fileIds: string[] }) =>
      setWidgetFiles(widgetId, fileIds),
    onSuccess: (_, { widgetId }) => {
      queryClient.invalidateQueries({ queryKey: ['widgets'] });
      queryClient.invalidateQueries({ queryKey: ['widget', widgetId] });
    },
  });
}
